// includes/js-src/inventory/equipment-grid.ts
//
// renderEquipmentSlotGrid(blob) — the client-side port of the equipment-slot grid that the PHP
// combined-list emitted just before "Inventory Equipped" (options/tables/creature/
// _inventory_equipped_equipment_slots_inc.php + display_*_inventory_thumbnail in functions.php).
// inventory→TS Phase 2, 2026-06-01: replaces ft_ld_ts_equipment_slots_html() — the server no longer
// ob_start()s render_inventory_thumbnail and ships HTML; it ships data (the blob), TS renders here.
//
// IMPORTANT — this is the DISPLAY-ONLY grid (PHP calls render_inventory_thumbnail with
// show_overlay:false), which in PHP gates OFF the overlay, HP bar, status effects AND the icons.
// So a cell is just: sized wrapper + thumbnail image (or an initials box), or a slot-icon fallback
// button when the slot is empty. That means this renderer needs ZERO template.js-resident deps
// (no SIZE_TYPES / hasStatusEffect / decodeHtmlEntitiesForPlainText) — only iconMap/escapeHtml
// (core-helpers.js) + the ft* bucket readers (item-arr-readers.js), both loaded before the
// list-display bundle that calls this. So it renders on first paint with no flash. It is NOT the
// drifted full render_inventory_thumbnail (that path is the XZ map's renderInventoryThumbnail).
//
// Picture source mirrors the list-display row renderer (primitives.ts): the enriched
// _ft_display.pic_url (list_display_inventory_thumbnail_url — walks the armor_classification
// carrier) is authoritative, then the entry's own primary picture.
import { bodyPairsLimbs, isPairedLimb } from '../shared/paired-limb-forms';
import { queueInventoryActivities, rememberInventoryActivities } from './don-doff-overlay';

import { EQUIPMENT_SLOTS as SLOT } from '../janus-engine/inventory/equipment-slots';

// natural_weapon_form catalog ids (includes/additions.php). Either form IS the creature's tail, so it
// grants the tail equipment slot without the has_tail layer being ticked (owner 2026-09-02). Twin of
// the PC-sheet inventory.ts constants.
const NW_FORM_TAIL = 'FABD5931-2BE5-4A39-BD45-62529717A62F';
const NW_FORM_STINGER = 'FAEFBA34-2176-46E5-911D-51580EF6DA74';

const RENDERED_SLOT_IDS = new Set<string>(Object.values(SLOT));

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;


// item_arr_uuid_slot() equivalent: coerce ftLayerValue('equipment_slot') (carrier-walked,
// shell-resolved) to a bare UUID string. ftLayerValue usually returns the UUID directly; dig the
// {items:{item_id}} shell / [0] wrapper defensively.
type EquipmentRecord = Record<string, unknown>;

function equipmentRecord(value: unknown): EquipmentRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as EquipmentRecord
        : null;
}

function equipmentCatalog(blob: unknown): Record<string, unknown> | null {
    const catalog = equipmentRecord(blob)?.catalog;
    return equipmentRecord(catalog);
}

function equipmentEntryId(entry: unknown): string {
    const record = equipmentRecord(entry);
    const items = equipmentRecord(record?.items);
    return String(items?.item_id ?? record?.item_id ?? '');
}

function slotUuidOf(v: unknown): string {
    if (v == null) return '';
    if (typeof v === 'string') { const s = v.trim(); return UUID_RE.test(s) ? s : ''; }
    if (Array.isArray(v)) return slotUuidOf(v[0]);
    if (typeof v === 'object') {
        const record = equipmentRecord(v);
        const items = equipmentRecord(record?.items);
        const rawItemId = items?.item_id;
        const rawDirectId = record?.item_id;
        const nested = (Array.isArray(rawItemId) ? rawItemId[0] : rawItemId)
            ?? (Array.isArray(rawDirectId) ? rawDirectId[0] : rawDirectId);
        return slotUuidOf(nested);
    }
    return '';
}

function entrySlotId(entry: unknown): string {
    return slotUuidOf(ftLayerValue(entry, 'equipment_slot'));
}

// Slot for an inventory INSTANCE: its `inventory.equipped` column IS the assigned slot UUID
// (item_arr_uuid_slot of the inventory-table `equipped`), '' when not equipped. This is the
// AUTHORITATIVE slot — it already encodes main/off-hand balancing the item's intrinsic
// equipment_slot layer can't express. Mirrors equippedSlotId() in the PC-sheet inventory tab.
function instanceEquippedSlotId(entry: unknown): string {
    return slotUuidOf(ftInventoryField(entry, 'equipped'));
}

function isRenderedSlotId(slotId: string): boolean {
    return slotId !== '' && RENDERED_SLOT_IDS.has(slotId);
}

// Resolve a (possibly THINNED) equipped inventory instance's display essentials — picture + name —
// from the ROOT blob's OWN catalog, which renderEquipmentSlotGrid always holds. Thinned instances
// drop their items/pictures buckets to the root catalog, and the generic readers bridge that through
// the GLOBAL window.FateTide caches — which on the static-creature inventory mount may not hold this
// blob (or aren't seeded yet at first paint), so the equipped cells rendered pictureless/blank.
// Stamping _ft_display.pic_url + an items shell here makes thumbCell resolve against in-blob data
// only (no cache dependency). Returns a shallow copy — never mutates the cached blob. Already-rich
// or already-enriched instances pass through untouched.
// Inline a base item's *_classification carrier ref (a bare-UUID catalog ref) with its FULL def
// from the blob's OWN root `.catalog`, returning a shallow copy whose carrier slot holds the full
// object. The picture/layer carrier-walk then descends it directly instead of via
// window.ftFindFullNestedItem / a warm objectCache (cold on a fresh item.php first paint). No-op
// without a catalog or a bare-UUID carrier. Per-entry twin of resolveClassificationCarriersFromCatalog
// (list-display render-item-list-display.ts) — see [[reference_equipment_slot_grid]].
const FT_CLASSIFICATION_CARRIER_LAYERS = ['weapon_classification', 'armor_classification', 'instrument_classification', 'weapon_ammo_classification'];
function inlineClassificationCarriers(def: unknown, catalog: unknown): unknown {
    const defRecord = equipmentRecord(def);
    const catalogRecord = equipmentRecord(catalog);
    if (!defRecord) return def;
    const ilt = equipmentRecord(defRecord.item_layer_types);
    if (!ilt || !catalogRecord) return def;
    let touched = false;
    const next: Record<string, unknown> = { ...ilt };
    const resolve = (ref: unknown): unknown => {
        if (typeof ref !== 'string' || ref === '') return ref;
        const c = catalogRecord[ref] || catalogRecord[ref.toUpperCase()] || catalogRecord[ref.toLowerCase()];
        const candidate = equipmentRecord(c);
        if (candidate && (candidate.items || candidate.item_layer_types)) { touched = true; return candidate; }
        return ref;
    };
    for (const k of FT_CLASSIFICATION_CARRIER_LAYERS) {
        const slot = next[k];
        if (Array.isArray(slot)) next[k] = slot.map(resolve);
        else if (typeof slot === 'string' && slot !== '') next[k] = resolve(slot);
    }
    return touched ? { ...defRecord, item_layer_types: next } : def;
}

function hydrateEquippedInstance(entry: unknown, rootBlob: unknown): unknown {
    const entryRecord = equipmentRecord(entry);
    if (!entryRecord) return entry;
    const existingDisplay = equipmentRecord(entryRecord._ft_display);
    if (existingDisplay?.pic_url) return entry;
    const cat = equipmentCatalog(rootBlob);
    const tplId = String(ftInventoryField(entry, 'inventory_item_id') ?? '');
    if (!cat || typeof cat !== 'object' || tplId === '') return entry;
    const def = equipmentRecord(cat[tplId] || cat[tplId.toUpperCase()] || cat[tplId.toLowerCase()]);
    if (!def) return entry;
    // A base weapon/armor (Greataxe, Mail Shirt) carries no own picture — its art lives on its
    // *_classification carrier, referenced as a bare-UUID. ftPrimaryPictureOf would resolve that
    // ref only via window.ftFindFullNestedItem (a warm objectCache), which is COLD on a fresh
    // item.php first paint, leaving those items pictureless. Inline the carrier from the blob's
    // OWN catalog first so the walk descends a full object directly (no cache dependency).
    const pic = ftPrimaryPictureOf(inlineClassificationCarriers(def, cat));
    const picUrl = String(equipmentRecord(pic)?.picture_url ?? '');
    const name = String(ftItemsCol(def, 'item_name') ?? ftInventoryField(entry, 'inventory_name') ?? '');
    const out: EquipmentRecord = { ...entryRecord, _ft_display: { ...(existingDisplay ?? {}), pic_url: picUrl } };
    // Ensure a resolvable item_name: instances are either fully thinned (no items bucket) or carry a
    // thinned items POINTER ({item_id} with no item_name) — both leave ftItemsCol blank without a cache.
    if (!out.items || typeof out.items !== 'object') {
        out.items = { item_id: tplId, item_name: name, item_type: ftItemsCol(def, 'item_type') ?? '' };
    } else {
        const outItems = equipmentRecord(out.items) ?? {};
        if (!outItems.item_name) out.items = { ...outItems, item_name: name };
    }
    return out;
}

// Resolve a raw `inventory_equipped` LAYER entry (not an inventory instance) to a full, carrier-inlined
// item def so entrySlotId (ftLayerValue 'equipment_slot') and thumbCell (picture/name) resolve from
// IN-BLOB data. On a catalog-deduped blob these entries are BARE-UUID refs (or thin {item_id} shells)
// pointing into the root .catalog — and ftLayerValue can't read a layer off a bare string, so every slot
// came up empty. This is the creature_template case: those items never run finalize_creature_arr's
// inventory-instance synthesis (the useInstances path below), so the grid falls back to this layer.
// Mirror of the retired PHP thumbnail helper's query_full_item_arr() resolve, done against the blob's own
// catalog (cache-independent, exactly like hydrateEquippedInstance / naturalArmorCell). The carrier inline
// lets a weapon/armor whose equipment_slot lives on its *_classification carrier (Longsword, Pistol,
// Brigandine) walk to its slot + art with no warm-cache dependency. Returns the entry untouched when it
// can't resolve (→ empty slot, unchanged behaviour).
function resolveEquippedLayerEntry(entry: unknown, blob: unknown): unknown {
    const cat = equipmentCatalog(blob);
    const catLookup = (id: string): unknown => {
        if (!cat || id === '') return null;
        return cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()] || null;
    };
    let node: unknown = entry;
    if (typeof entry === 'string') {
        if (entry === '') return entry;
        // Legacy "uuid|qty-or-template" packing (resolve_inventory_layer_list_entry) — the id is the head.
        const id = entry.indexOf('|') !== -1 ? (entry.split('|', 2)[0] ?? '') : entry;
        const def = catLookup(id);
        if (!def || typeof def !== 'object') return entry; // unresolved → leave as-is (empty slot)
        node = def;
    } else if (equipmentRecord(entry)) {
        // A deduped SHELL ({items:{item_id}} but no real item_layer_types) can't carrier-walk to its slot;
        // prefer the fuller catalog def when the entry is thin.
        const entryRecord = equipmentRecord(entry)!;
        const ilt = entryRecord.item_layer_types;
        const thin = !ilt || typeof ilt !== 'object' || Object.keys(ilt).length === 0;
        if (thin) {
            const id = equipmentEntryId(entry);
            const def = catLookup(id);
            if (def && typeof def === 'object') node = def;
        }
    }
    return (node && cat) ? inlineClassificationCarriers(node, cat) : node;
}

function equippedLayerEntryItemId(entry: unknown): string {
    if (typeof entry === 'string') {
        const id = entry.indexOf('|') !== -1 ? (entry.split('|', 2)[0] ?? '') : entry;
        return UUID_RE.test(id) ? id.toUpperCase() : '';
    }
    if (!entry || typeof entry !== 'object') return '';
    const raw = equipmentEntryId(entry);
    return UUID_RE.test(raw) ? raw.toUpperCase() : '';
}

function inventoryInstanceItemId(entry: unknown): string {
    const raw = String(ftInventoryField(entry, 'inventory_item_id') ?? '');
    return UUID_RE.test(raw) ? raw.toUpperCase() : '';
}

function pictureUrlOf(entry: unknown): string {
    const disp = equipmentRecord(equipmentRecord(entry)?._ft_display);
    if (disp?.pic_url) return String(disp.pic_url);
    const pic = ftPrimaryPictureOf(entry);
    const purl = equipmentRecord(pic)?.picture_url;
    if (purl) return String(purl);
    return '';
}

/** The id a click on an occupied cell OPENS — the same rule the sibling Equipped list rows already
 *  use (findStaticEquippedSyntheticId, list-display primitives.ts):
 *   - a REAL inventory instance (live encounter creature) → its inventory_id: discern_item_id()
 *     (open_table_item_inc.php) resolves it, so the panel shows the instance, enchantment and all.
 *   - a STATIC item-creature's projected instance (enchantment_and_inventory_layering.php stamps
 *     `inventory_static_display`) has an md5-DERIVED inventory_id that exists in NO table, so the
 *     server 404s it ("No stored record was found for that ID"). Open it only when the projection
 *     actually carries an enchantment the base item lacks — that is the case worth the cache-only
 *     render (render-from-cache.ts isStaticDisplayInventoryNode) — else open the base catalog item,
 *     which is server-resolvable and identical in content.
 *   - the raw `inventory_equipped` LAYER shape (creature_template) has no instance at all → base id. */
function openInfoIdOf(entry: unknown): string {
    const invId = String(ftInventoryField(entry, 'inventory_id') ?? '');
    const baseId = inventoryInstanceItemId(entry) || equippedLayerEntryItemId(entry);
    if (!UUID_RE.test(invId)) return baseId;
    const sd = ftInventoryField(entry, 'inventory_static_display');
    const isSynthetic = sd === 1 || sd === '1' || sd === true;
    const hasEnchantment = String(ftInventoryField(entry, 'enchantment_template_id') ?? '') !== '';
    if (isSynthetic && !hasEnchantment && baseId !== '') return baseId;
    return invId.toUpperCase();
}

// One equipped cell — matches render_inventory_thumbnail(show_overlay:false): sized wrapper +
// img.img-thumbnail (the same classes get_thumbnail emits) or an initials box. Drag is
// non-functional (TokenDragController.initDrag() requires data-draggable_id, which this cell does
// not carry); the draggable* classes are kept for visual parity but the native draggable attribute
// is intentionally omitted.
function thumbCell(entry: unknown): string {
    const picUrl = pictureUrlOf(entry);
    const name = String(ftItemsCol(entry, 'item_name') ?? '');
    const inner = picUrl !== ''
        ? `<img src="${escapeHtml(picUrl)}" class="img-thumbnail d-block w-100" alt="${escapeHtml(name)}">`
        : `<div class="bg-warning border border-dark rounded d-flex justify-content-center align-items-center" style="width:100%;height:100%;">`
          + `<span class="fw-bold h4 text-dark">${escapeHtml(name.slice(0, 2).toUpperCase())}</span></div>`;
    // Name tooltip on hover — instant bootstrap tooltip (data-bs-original-title), same pattern
    // as the natural-armor/weapon cells; never the slow native title=.
    const tooltip = name !== ''
        ? ` data-bs-toggle="tooltip" data-bs-original-title="${escapeHtml(name)}"`
        : '';
    // The cell has to be OPENABLE. template.js's document-level delegation matches `.inventory-item`
    // and routes a double-click to attemptOpenItemInfo(), which needs an identity attribute this
    // display-only cell never emitted → every equipped item answered "No valid ID found." (owner
    // 2026-09-08). Own the click here instead, exactly like the natural-weapon / natural-armor cells
    // below: single OR double click opens the item. event.stopPropagation() keeps the delegated
    // handler from ALSO running, so clicking the paper doll never toggles a combat `.targeted`
    // selection (targetObject) onto an equipped item.
    // `event.detail < 2` swallows the SECOND click of a double-click so one gesture is one open
    // (the delegated handler that used to own double-click is bypassed here).
    const openId = openInfoIdOf(entry);
    const open = openId !== ''
        ? ` role="button" onclick="event.stopPropagation(); if (event.detail < 2) openItemInfo('${escapeHtml(openId)}');"`
        : '';
    const inventoryId = String(ftInventoryField(entry, 'inventory_id') ?? '');
    const identity = inventoryId ? ` data-inventory_id="${escapeHtml(inventoryId)}"` : '';
    return `<div class="inventory-item dropzone draggable draggable-line"${identity} style="opacity:1;display:block;"${tooltip}${open}>`
        + `<div class="position-relative d-block w-100" style="z-index:0; --tw:68px; --th:68px;">`
        + `<div class="d-flex align-items-center justify-content-center" style="width:68px;height:68px;">${inner}</div>`
        + `</div></div>`;
}

/** Empty slot — the slot's own icon, larger and borderless (owner direction 2026-06-10; kept in
 *  sync with the PC-sheet inventory.ts slotButton — [[reference_equipment_slot_grid]] universal rule). */
function slotButton(slotId: string): string {
    return `<div class="d-flex align-items-center justify-content-center" style="width:68px;height:68px;opacity:.55;">${iconMap(slotId, 46, 46)}</div>`;
}

// display_inventory_equipped_inventory_thumbnail(): nth (slot_number) entry matching the slot, else
// the slot-icon fallback button. `slotOf` resolves an entry's slot — entrySlotId for the raw
// inventory_equipped layer, instanceEquippedSlotId for the inventory-instance bucket.
function cellForSlot(entries: unknown[], slotId: string, slotNumber = 1, slotOf: (e: unknown) => string = entrySlotId): string {
    let count = 0;
    for (const e of entries) {
        if (slotOf(e) === slotId) {
            count++;
            if (count !== slotNumber) continue;
            return thumbCell(e);
        }
    }
    return slotButton(slotId);
}

// display_inventory_equipped_main_and_off_hand_inventory_thumbnail(): both_hands shows in BOTH
// columns (right side at 0.5 opacity); otherwise the first main/off entry on the left, the next on
// the right. main_hand and off_hand are treated interchangeably for left/right assignment (mirrors PHP).
function gridCell(html: string, key: string, cells?: Map<string, string>): string {
    cells?.set(key, html);
    return `<div class="col-4 m-1" data-ft-equipment-cell="${key}">${html}</div>`;
}

function handsRow(entries: unknown[], slotOf: (e: unknown) => string = entrySlotId, blob: unknown = null, cells?: Map<string, string>): string {
    let both: unknown | null = null, main: unknown | null = null, off: unknown | null = null;
    for (const e of entries) {
        const s = slotOf(e);
        if (s === '') continue;
        if (s === SLOT.both_hands) { both = both || e; continue; }
        if (!main && (s === SLOT.main_hand || s === SLOT.off_hand)) { main = e; continue; }
        if (!off && (s === SLOT.main_hand || s === SLOT.off_hand)) { off = e; continue; }
    }
    // Intrinsic natural-weapon fallbacks for hands the equipped inventory left bare (twin of the
    // PC-sheet inventory.ts). A both-hands natural weapon fills BOTH cells (like a both-hands inventory
    // weapon) but only when NO inventory occupies either hand.
    const nwBoth = (!both && !main && !off) ? naturalWeaponCell(blob, SLOT.both_hands) : '';

    // Single-hand naturals are assigned as a SET (owner 2026-09-02): one claw with both hands bare shows
    // main + a ghosted off-hand copy (the two-handed treatment — one limb drawn twice, NOT a second
    // weapon), and equipping a main-hand weapon does not take the claw away — it slides into the still-free
    // off hand as a REAL occupant. Mirrors buildEquipmentColumn in the PC-sheet inventory.ts.
    const mainFree = !both && !main;
    const offFree = !both && !off;
    let nwMain: ResolvedNaturalWeapon | null = null;
    let nwOff: ResolvedNaturalWeapon | null = null;
    let nwOffIsMirror = false;
    if (nwBoth === '' && (mainFree || offFree)) {
        const pool = resolveNaturalWeapons(blob)
            .filter((n) => n.slotId === SLOT.main_hand || n.slotId === SLOT.off_hand);
        const take = (want: string): ResolvedNaturalWeapon | null => {
            const i = pool.findIndex((n) => n.slotId === want);
            return i >= 0 ? (pool.splice(i, 1)[0] ?? null) : null;
        };
        if (mainFree) nwMain = take(SLOT.main_hand);
        if (offFree) nwOff = take(SLOT.off_hand);
        if (mainFree && !nwMain && pool.length > 0) nwMain = pool.shift() ?? null;
        if (offFree && !nwOff && pool.length > 0) nwOff = pool.shift() ?? null;
        // A PAIRED limb (fist/slam, claw) is two weapons — both cells real, no ghost (owner 2026-09-03;
        // shared/paired-limb-forms.ts, twin of calculate_equipped_counts.php).
        if (mainFree && offFree && (nwMain === null) !== (nwOff === null)) {
            const solo = nwMain ?? nwOff!;
            nwMain = solo;
            nwOff = solo;
            nwOffIsMirror = !(isPairedLimb(solo.formId, solo.uuid) && bodyPairsLimbs(ftLayerValue(blob, 'quadruped'), ftItemsCol(blob, 'item_type')));
        }
    }

    let left = '', right = '';
    if (both || main) left += thumbCell(both || main);
    else if (nwBoth !== '') left += nwBoth;
    else if (nwMain) left += naturalWeaponCellFor(nwMain, SLOT.main_hand);
    else left += slotButton(SLOT.main_hand);
    if (both || off) {
        // both-hands "ghost" off-hand tile: heavily faded duplicate of the main-hand tile. Kept in sync with PHP (functions.php).
        const opacityStyle = both ? ' style="opacity: 0.3;"' : '';
        right += `<div${opacityStyle}>` + thumbCell(both || off) + '</div>';
    } else if (nwBoth !== '') {
        right += `<div style="opacity: 0.3;">${nwBoth}</div>`;
    } else if (nwOff) {
        const cell = naturalWeaponCellFor(nwOff, SLOT.off_hand);
        right += nwOffIsMirror ? `<div style="opacity: 0.3;">${cell}</div>` : cell;
    } else {
        right += slotButton(SLOT.off_hand);
    }
    return '<div class="d-flex align-items-center justify-content-center">'
        + gridCell(left, SLOT.main_hand, cells) + gridCell('&nbsp;', 'hands-spacer', cells)
        + gridCell(right, SLOT.off_hand, cells) + '</div>';
}

/**
 * Intrinsic natural-armor cell (display-only grid twin of the PC-sheet inventory.ts naturalArmorTile).
 * The `natural_armor` layer entry is almost always a bare UUID REF — a creature's intrinsic natural
 * armor lives in computed.creature.abilities.natural_armor as a stripped ref (e.g. "Natural Armor +5"),
 * and that item has NO own picture: its art lives on its armor_classification carrier ("Natural
 * Armors"). A bare string can't be walked to that carrier, so the old `{...e}` spread produced an empty
 * box and a dead click. Resolve the ref to its full nested catalog node so pictureUrlOf (→
 * ftPrimaryPictureOf, which walks the carrier) finds the real art, the tooltip/name resolve, and the
 * cell clicks through to the correct item info. '' when the creature has no natural armor.
 */
function naturalArmorCell(blob: unknown): string {
    // Resolve refs against the blob's OWN catalog (cache-independent) — same reason hydrateEquippedInstance
    // does: on the static item-creature page the global FateTide cache is COLD at first paint, so the
    // ftFindFullNestedItem path below returns null and the cell fell back to the bare slot glyph (owner:
    // "borked again", 2026-06-29). The full def is always sitting in blob.catalog; read it from there.
    const cat = equipmentCatalog(blob);
    const catLookup = (id: string): unknown => {
        if (!cat || id === '') return null;
        return cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()] || null;
    };
    for (const entry of (ftLayerEntries(blob, 'natural_armor') || [])) {
        if (entry === null || entry === undefined) continue;
        const uuid = typeof entry === 'string'
            ? entry
            : equipmentEntryId(entry);
        if (uuid === '') continue;
        let node: unknown = equipmentRecord(entry) ? entry : null;
        if (!node) node = catLookup(uuid);
        if (!node && typeof window.ftFindFullNestedItem === 'function') {
            node = window.ftFindFullNestedItem(uuid) || null;
        }
        // The natural armor's art lives on its armor_classification carrier ("Natural Armors"), referenced
        // as a bare-UUID. Inline that carrier from the blob's own catalog so the picture walk (pictureUrlOf
        // → ftPrimaryPictureOf → ftPicturesOf carrier descent) resolves in-blob with no cache dependency.
        if (node && cat) node = inlineClassificationCarriers(node, cat);
        let name = node ? String(ftItemsCol(node, 'item_name') ?? '') : '';
        if (name === '' && typeof ftItemNameById === 'function') {
            name = String(ftItemNameById(uuid) || '');
        }
        if (name === '') name = 'Natural Armor';
        const url = node ? pictureUrlOf(node) : '';
        let art: string;
        let hasArt = false; // true only when the art DISTINGUISHES the item from the slot glyph
        if (url !== '') {
            art = `<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" class="img-thumbnail" style="width:66px;height:66px;object-fit:cover;">`;
            hasArt = true;
        } else {
            art = iconMap('natural_armor', 46, 46);
        }
        // Slot-type corner badge only when the main art is a real picture (else it would just duplicate
        // the slot glyph — the doubled-icon look). Mirrors the PC-sheet naturalArmorTile.
        const slotBadge = hasArt
            ? `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(SLOT.natural_armor, 15, 15)}</span>`
            : '';
        return `<div class="d-flex align-items-center justify-content-center position-relative" role="button" style="width:68px;height:68px;"`
            + ` onclick="openItemInfo('${escapeHtml(uuid)}');"`
            + ` data-bs-toggle="tooltip" data-bs-original-title="${escapeHtml(name)}">${art}${slotBadge}</div>`;
    }
    return '';
}

/** Merged intrinsic natural weapons: pristine `natural_weapon` layer (Bite/Claw) + the DERIVED
 *  computed.creature.natural_weapon grants (the Living Weapon slam), deduped by UUID. ftLayerEntries does
 *  not bridge the computed grant, so a creature whose only natural weapon is the derived slam needs this.
 *  Client twin of PHP creature_natural_weapon_entries. */
function naturalWeaponEntries(blob: unknown): unknown[] {
    const base = ftLayerEntries(blob, 'natural_weapon') || [];
    const computed = equipmentRecord(equipmentRecord(blob)?.computed);
    const creature = equipmentRecord(computed?.creature);
    const derived = creature?.natural_weapon;
    if (!Array.isArray(derived) || derived.length === 0) return base;
    const uuidOf = (e: unknown): string => typeof e === 'string'
        ? e
        : equipmentEntryId(e);
    const seen: Record<string, boolean> = {};
    const out: unknown[] = [];
    for (const e of base) { const u = uuidOf(e); if (u !== '') seen[u] = true; out.push(e); }
    for (const d of derived) { const u = uuidOf(d); if (u !== '' && seen[u]) continue; if (u !== '') seen[u] = true; out.push(d); }
    return out;
}

/**
 * Intrinsic natural-weapon cell (display-only grid twin of the PC-sheet inventory.ts naturalWeaponTile).
 * Natural weapons (claws / bite / slam / tail) live on the `natural_weapon` layer as bare-UUID REFs and
 * occupy a hand by their OWN equipment_slot — never as an equipped inventory row — so a barehanded
 * creature's hand cells drew empty. Resolve the ref whose equipment_slot names the requested hand slot to
 * its full nested catalog node (so pictureUrlOf walks the weapon_classification carrier for art), then
 * render a click-through cell whose corner badge is the hand it occupies — the "icon changes with how it's
 * equipped" the owner asked for. '' when the creature has no natural weapon for that hand.
 */
type ResolvedNaturalWeapon = { uuid: string; node: unknown; slotId: string; formId: string };

/** Resolve every intrinsic natural weapon ONCE, deduped, with its own equipment_slot + natural_weapon_form.
 *  Twin of resolveNaturalWeapons() in the PC-sheet inventory.ts, but refs resolve against the blob's OWN
 *  catalog first (cache-independent — the global FateTide cache is cold at first paint here). */
function resolveNaturalWeapons(blob: unknown): ResolvedNaturalWeapon[] {
    const cat = equipmentCatalog(blob);
    const catLookup = (id: string): unknown => {
        if (!cat || id === '') return null;
        return cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()] || null;
    };
    const out: ResolvedNaturalWeapon[] = [];
    const seen: Record<string, boolean> = {};
    for (const entry of naturalWeaponEntries(blob)) {
        if (entry === null || entry === undefined) continue;
        const uuid = typeof entry === 'string' ? entry : equipmentEntryId(entry);
        if (uuid === '' || seen[uuid.toUpperCase()]) continue;
        seen[uuid.toUpperCase()] = true;
        // Only trust an entry that already carries item_layer_types; a bare-UUID ref OR a thin
        // computed-grant stub (the derived slam) has no equipment_slot to read, so resolve the UUID
        // against the blob's own catalog (cache-independent) then the warm cache.
        let node: unknown = equipmentRecord(entry)?.item_layer_types ? entry : null;
        if (!node) node = catLookup(uuid);
        if (!node && typeof window.ftFindFullNestedItem === 'function') {
            node = window.ftFindFullNestedItem(uuid) || null;
        }
        // Inline the weapon_classification carrier from the blob's own catalog so pictureUrlOf resolves
        // in-blob (no cache dependency), same as naturalArmorCell.
        if (node && cat) node = inlineClassificationCarriers(node, cat);
        out.push({
            uuid,
            node,
            slotId: entrySlotId(node).toUpperCase(),
            formId: slotUuidOf(node ? ftLayerValue(node, 'natural_weapon_form') : null).toUpperCase(),
        });
    }
    return out;
}

/** A creature has a TAIL — and so gets the tail slot on the paper doll — when it carries the authored
 *  `has_tail` anatomy layer or wields a natural weapon that IS its tail (tail-slotted, or a tail / stinger
 *  natural_weapon_form). Twin of hasTailAnatomy() in the PC-sheet inventory.ts. */
function hasTailAnatomy(blob: unknown, naturals: ResolvedNaturalWeapon[]): boolean {
    let flag: unknown = ftLayerValue(blob, 'has_tail');
    if (Array.isArray(flag)) flag = flag[0];
    if (Number(flag ?? 0) > 0) return true;
    return naturals.some((n) => n.slotId === SLOT.tail || n.formId === NW_FORM_TAIL || n.formId === NW_FORM_STINGER);
}

/** Render ONE resolved natural weapon. `badgeSlotId` is the slot the CELL represents, which is not always
 *  the weapon's own slot — a lone claw mirrored into the empty off hand badges as the off hand. */
function naturalWeaponCellFor(nw: ResolvedNaturalWeapon, badgeSlotId: string): string {
    const { uuid, node } = nw;
    let name = node ? String(ftItemsCol(node, 'item_name') ?? '') : '';
    if (name === '' && typeof ftItemNameById === 'function') {
        name = String(ftItemNameById(uuid) || '');
    }
    if (name === '') name = 'Natural Weapon';
    const url = node ? pictureUrlOf(node) : '';
    // Own art distinguishes the weapon; else the generic natural_weapon glyph (the common bare-UUID case).
    const art = url !== ''
        ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" class="img-thumbnail" style="width:66px;height:66px;object-fit:cover;">`
        : iconMap('natural_weapon', 46, 46);
    // Corner badge = the SLOT the natural weapon occupies (never the same glyph as the face), so it
    // always shows — the equip-state cue.
    const slotBadge = `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(badgeSlotId, 15, 15)}</span>`;
    return `<div class="d-flex align-items-center justify-content-center position-relative" role="button" style="width:68px;height:68px;"`
        + ` onclick="openItemInfo('${escapeHtml(uuid)}');"`
        + ` data-bs-toggle="tooltip" data-bs-original-title="${escapeHtml(name)}">${art}${slotBadge}</div>`;
}

/** The natural weapon whose OWN equipment_slot names `slotId` (head Bite, tail Slap, both-hands maw), or
 *  '' when there is none. The HAND cells no longer route through here — handsRow assigns them as a set. */
function naturalWeaponCell(blob: unknown, slotId: string): string {
    const nw = resolveNaturalWeapons(blob).find((n) => n.slotId === slotId);
    return nw ? naturalWeaponCellFor(nw, slotId) : '';
}

/**
 * Render the equipment-slot grid from the compiled item/creature blob. Returns '' when there is no
 * equipped inventory (mirrors PHP, which only emitted the grid when inventory_equipped was present).
 */
export function renderEquipmentSlotGrid(blob: unknown, cells?: Map<string, string>): string {
    rememberInventoryActivities(blob);
    queueInventoryActivities();
    // Equipped gear arrives in one of two shapes:
    //   - inventory INSTANCES in the `inventory` bucket — live encounter creatures, AND static item
    //     creatures since finalize_creature_arr now synthesizes them (so ENCHANTED equipped gear,
    //     whose inventory_equipped entries are rich carriers the layer-ref expander never full-nests,
    //     fills its slots). Each carries inventory.equipped = the AUTHORITATIVE slot UUID.
    //   - the raw `inventory_equipped` layer — plain library/item cards: slot via equipment_slot.
    // Prefer instances whenever any equipped one is present.
    const rawInventory = equipmentRecord(blob)?.inventory;
    const invInstances: unknown[] = Array.isArray(rawInventory)
        ? rawInventory.filter((e) => instanceEquippedSlotId(e) !== '')
        : [];
    const useInstances = Array.isArray(rawInventory);
    // Hydrate equipped instances against the root blob's catalog so their thumbnails resolve without
    // depending on the global FateTide caches (which may not hold this blob at first paint).
    const rawEntries = (ftLayerEntries(blob, 'inventory_equipped') || [])
        .map((e) => resolveEquippedLayerEntry(e, blob));
    const entries: unknown[] = useInstances
        // Native instances are the complete worn answer, including an authored
        // empty. Never reintroduce a losing provider through raw-layer fallback.
        ? invInstances.map((e) => hydrateEquippedInstance(e, blob))
        // Raw-layer path (creature_template + plain library cards): resolve bare-UUID / shell entries
        // against the blob's own catalog so entrySlotId + thumbCell read equipment_slot / art in-blob.
        : rawEntries;
    const slotOf = useInstances
        ? (e: unknown) => instanceEquippedSlotId(e) || entrySlotId(e)
        : entrySlotId;

    // Intrinsic natural armor is rendered by its own resolver (the entry is a bare UUID ref the generic
    // thumbCell can't picture/click). Built separately so a creature with ONLY natural armor still draws
    // the grid.
    const naturalArmorHtml = naturalArmorCell(blob);
    // A creature whose only "gear" is its body (natural weapon, no equipped inventory, no natural armor)
    // must still draw the grid so the hand cells can show the claw/slam. Cheap presence probe across the
    // three hand slots — the same resolver handsRow uses.
    const naturals = resolveNaturalWeapons(blob);
    const hasNaturalWeapon = naturals.some((n) => n.slotId === SLOT.main_hand
        || n.slotId === SLOT.off_hand
        || n.slotId === SLOT.both_hands
        || n.slotId === SLOT.head
        || n.slotId === SLOT.tail);
    const hasTail = hasTailAnatomy(blob, naturals);
    if (!cells && entries.length === 0 && naturalArmorHtml === '' && !hasNaturalWeapon && !hasTail) return '';

    // A head-slotted natural weapon (a Bite) shows in the head cell when no equipped inventory occupies
    // the head — the display-only twin of the hands' natural-weapon fallback in handsRow (owner request
    // 2026-07-09). An equipped head item always wins.
    const headHasInv = entries.some((e) => slotOf(e) === SLOT.head);
    const headNwHtml = headHasInv ? '' : naturalWeaponCell(blob, SLOT.head);
    // Same for a TAIL natural weapon in the tail cell (owner 2026-09-02).
    const tailHasInv = entries.some((e) => slotOf(e) === SLOT.tail);
    const tailNwHtml = tailHasInv ? '' : naturalWeaponCell(blob, SLOT.tail);

    const cell = (key: string, html: string) => gridCell(html, key, cells);
    const row  = (cells: string[]) => '<div class="d-flex align-items-center justify-content-center">' + cells.join('') + '</div>';
    const cs   = (slotId: string, n = 1) => cellForSlot(entries, slotId, n, slotOf);

    let g = '<div class="d-flex align-items-center justify-content-center m-2 border rounded"><div>';
    g += row([cell(SLOT.air_around, cs(SLOT.air_around)), cell(SLOT.head, headNwHtml || cs(SLOT.head)), cell(SLOT.neck, cs(SLOT.neck))]);
    g += row([cell(SLOT.natural_armor, naturalArmorHtml || cs(SLOT.natural_armor)), cell(SLOT.armor, cs(SLOT.armor)), cell(SLOT.back, cs(SLOT.back))]);
    g += row([cell(SLOT.bracers, cs(SLOT.bracers)), cell(SLOT.clothing, cs(SLOT.clothing)), cell(SLOT.cloak, cs(SLOT.cloak))]);
    g += row([cell(SLOT.ring + ':1', cs(SLOT.ring, 1)), cell(SLOT.breast, cs(SLOT.breast)), cell(SLOT.ring + ':2', cs(SLOT.ring, 2))]);
    g += row([cell(SLOT.waist + ':1', cs(SLOT.waist, 1)), cell(SLOT.waist + ':2', cs(SLOT.waist, 2)), cell(SLOT.waist + ':3', cs(SLOT.waist, 3))]);
    const tailCell = hasTail ? (tailNwHtml || cs(SLOT.tail)) : '';
    g += row([cell(SLOT.tail, tailCell), cell(SLOT.legs, cs(SLOT.legs)), cell(SLOT.feet, cs(SLOT.feet))]);
    g += handsRow(entries, slotOf, blob, cells);
    g += '</div></div>';
    return g;
}

const equipmentMounts = new WeakMap<HTMLElement, Map<string, { html: string; node: HTMLElement }>>();

/** Adopt once, then replace only the contents of changed keyed slots/cards. */
export function patchEquipmentSlotGrid(mount: HTMLElement, blob: unknown): HTMLElement[] {
    const cells = new Map<string, string>();
    const html = renderEquipmentSlotGrid(blob, cells);
    let held = equipmentMounts.get(mount);
    if (!held || !mount.contains(held.values().next().value?.node ?? null)) {
        mount.innerHTML = html;
        held = new Map();
        for (const node of mount.querySelectorAll<HTMLElement>('[data-ft-equipment-cell]')) {
            const key = node.dataset.ftEquipmentCell!;
            held.set(key, { html: cells.get(key)!, node });
        }
        equipmentMounts.set(mount, held);
        return [mount];
    }
    const changed: HTMLElement[] = [];
    for (const [key, content] of cells) {
        const cell = held.get(key)!;
        if (cell.html === content) continue;
        cell.node.innerHTML = content;
        cell.html = content;
        changed.push(cell.node);
    }
    return changed;
}
